import React, { useEffect, useState } from 'react';
import {
  StyleSheet, View, Text, Animated, Easing,
} from 'react-native';
import Moment from 'moment';
import { useSelector } from 'react-redux';
import Colors from '../constants/Colors';
import HappyScoreIndicator from './HappyScoreIndicator';
import SectionHeader from './SectionHeader';
import TopSectionInsights from './TopSectionInsights';
import HappyDetailsGraphWeb, { happyScoreMetrics } from '../graphs/HappyDetailsGraphWeb';
import { DateRange } from '../redux/dateRangeSlice';
import { HappyScoreCategory, HappyScoreCategoryTitles } from '../constants/Types';
import GetMetricHappyScoreData from '../requests/Sensors/GetMetricHappyScoreData';
import { ChartDataPoint } from '../graphs/axisUtils';

export const getAverageFromGraphData = (data: { value: number | null, date: string }[]) => {
  if (!data) return null;
  const values: number[] = data.map((point) => point.value).filter(Number.isFinite);
  const average: number = values
    .reduce((total: number, value: number) => total + value, 0) / values.length;
  return Number.isFinite(average) ? Math.floor(average) : null;
};

const defaultAnimationDuration: number = 300;

export const screenMaxWidth: number = 1840;
const maxWidth: number = screenMaxWidth - 74;

const animation = (
  animatedValue: Animated.Value,
  toValue: number,
  duration: number = defaultAnimationDuration,
): Animated.CompositeAnimation => Animated.timing(animatedValue, {
  duration,
  useNativeDriver: true,
  easing: Easing.ease,
  toValue,
});

export interface TopSectionProps {
  still?: boolean;
  screen: HappyScoreCategory,
  liveEnabled?: boolean;
}

const TopSection = ({ still, screen }: TopSectionProps) => {
  const graphIndex: number = happyScoreMetrics.map((item) => item.screen).indexOf(screen);
  const range: DateRange = useSelector((state: { dateRange: DateRange }) => (state.dateRange));
  const compareRange: DateRange = useSelector((state:
    { dateComparisonRange: DateRange }) => (state.dateComparisonRange));
  const [graphData, setGraphData] = useState<ChartDataPoint[][]>(
    [[], [], [], [], [], [], [], [], []],
  );
  const [graphCompareData, setGraphCompareData] = useState<ChartDataPoint[][]>(
    [[], [], [], [], [], [], [], [], []],
  );
  const [liveScore, setLiveScore] = useState<number | null>(null);
  const [average, setAverage] = useState<number | null>(null);
  const [todayScore, setTodayScore] = useState<number | null>(null);
  const [yesterdayScore, setYesterdayScore] = useState<number | null>(null);
  const [scoreCompared, setScoreCompared] = useState<number | null>(null);
  const [liveScoreAnimatedValue] = useState(new Animated.Value(0));
  const [scoreComparedAnimatedValue] = useState(new Animated.Value(0));
  const [sectionHeaderDefaultShown, setSectionHeaderDefaultShown] = useState<boolean>(true);
  const [defaultContentShown, setDefaultContentShown] = useState(sectionHeaderDefaultShown);
  const [topSectionHeight] = useState(new Animated.Value(327));
  const [happyScoreIndicatorSize] = useState(new Animated.Value(1));
  const [happyScoreIndicatorOffset] = useState(new Animated.Value(-60));
  const [topSectionMarginStart] = useState(new Animated.Value(60));
  const [sectionHeaderPaddingStart] = useState(new Animated.Value(172));
  const [graphContainerHeight] = useState(new Animated.Value(0));
  const [graphContainerMarginTop] = useState(new Animated.Value(0));
  const [graphContainerOpacity] = useState(new Animated.Value(0));
  const scoreAnimation: Animated.CompositeAnimation = Animated.parallel([
    Animated.timing(liveScoreAnimatedValue, {
      duration: 1000,
      useNativeDriver: true,
      easing: (time: number) => Easing.ease(time),
      toValue: liveScore,
    }),
    Animated.timing(scoreComparedAnimatedValue, {
      duration: 1000,
      useNativeDriver: true,
      easing: (time: number) => Easing.ease(time),
      toValue: scoreCompared,
    }),
  ]);

  // useEffect(() => {
  //   getClientDetails().then((response) => {
  //     let match = false;
  //     Object.keys(response?.clientDetails?.happy_score_insights ?? []).forEach((item) => {
  //       const split = item.split('_');
  //       const id = split[0];
  //       if (!match && routeName.includes(id) && (routeName === 'it' || id !== 'it')) {
  //         match = true;
  //         setDescription(response.clientDetails.happy_score_insights[item].text);
  //       }
  //     });
  //   });
  // }, []);

  useEffect(() => {
    if (!happyScoreMetrics[graphIndex]) return;
    GetMetricHappyScoreData({
      metric: happyScoreMetrics[graphIndex].metric,
      range: {
        start: Moment().subtract(1, 'day').startOf('day').toDate(),
        end: Moment().subtract(1, 'day').endOf('day').toDate(),
      },
    }).then((data) => setYesterdayScore(getAverageFromGraphData(data)));
    GetMetricHappyScoreData({
      metric: happyScoreMetrics[graphIndex].metric,
      range: {
        start: Moment().startOf('day').toDate(),
        end: Moment().endOf('day').toDate(),
      },
    }).then((data) => {
      setLiveScore(data?.[data.length - 1]?.value);
      setTodayScore(getAverageFromGraphData(data));
    });
  }, [graphIndex]);

  const updateGraphData = (compare: boolean = false) => {
    const { start, end } = compare ? compareRange : range;
    const newGraphData: any[][] = happyScoreMetrics.map(() => []);
    if (compare) setGraphCompareData(newGraphData);
    else setGraphData(newGraphData);
    if (!start || !end) {
      if (compare) setScoreCompared(null);
      else setAverage(null);
      return;
    }
    GetMetricHappyScoreData({ metric: happyScoreMetrics[graphIndex].metric, range: { start, end } })
      .then((data: { value, date }[]) => {
        if (compare) setScoreCompared(getAverageFromGraphData(data));
        else setAverage(getAverageFromGraphData(data));
        newGraphData[graphIndex]
          .push(...data.map(({ value, date }, index: number) => ({ value, date, index })));
        if (compare) setGraphCompareData([...newGraphData]);
        else setGraphData([...newGraphData]);
      });
  };

  useEffect(() => updateGraphData(false), [range]);

  useEffect(() => updateGraphData(true), [compareRange]);

  useEffect(() => {
    const topSectionHeightAnimation: Animated.CompositeAnimation = animation(
      topSectionHeight,
      sectionHeaderDefaultShown ? 327 : 71,
    );
    const happyScoreIndicatorSizeAnimation: Animated.CompositeAnimation = animation(
      happyScoreIndicatorSize,
      sectionHeaderDefaultShown ? 1 : 0.59,
    );
    const happyScoreIndicatorOffsetAnimation: Animated.CompositeAnimation = animation(
      happyScoreIndicatorOffset,
      sectionHeaderDefaultShown ? -60 : -80,
    );
    const topSectionMarginStartAnimation: Animated.CompositeAnimation = animation(
      topSectionMarginStart,
      sectionHeaderDefaultShown
        // ? 500 * Math.min(1, (Dimensions.get('window').width - 252) / 1188) - 40
        ? 60 : 42,
    );
    const sectionHeaderPaddingStartAnimation: Animated.CompositeAnimation = animation(
      sectionHeaderPaddingStart,
      sectionHeaderDefaultShown ? 172 : 102,
    );
    const graphContainerHeightAnimation: Animated.CompositeAnimation = animation(
      graphContainerHeight,
      sectionHeaderDefaultShown ? 0 : 290,
    );
    const graphContainerMarginTopAnimation: Animated.CompositeAnimation = animation(
      graphContainerMarginTop,
      sectionHeaderDefaultShown ? 0 : 16,
    );
    const graphContainerOpacityAnimation: Animated.CompositeAnimation = animation(
      graphContainerOpacity,
      sectionHeaderDefaultShown ? 0 : 1,
    );
    Animated.parallel([
      topSectionHeightAnimation,
      happyScoreIndicatorSizeAnimation,
      happyScoreIndicatorOffsetAnimation,
      topSectionMarginStartAnimation,
      sectionHeaderPaddingStartAnimation,
      graphContainerHeightAnimation,
      graphContainerMarginTopAnimation,
      graphContainerOpacityAnimation,
    ]).start();
    const timeout = setTimeout(() => {
      setDefaultContentShown(sectionHeaderDefaultShown);
      clearTimeout(timeout);
    }, defaultAnimationDuration);
  }, [sectionHeaderDefaultShown]);

  useEffect(() => scoreAnimation.start(), [liveScore, scoreCompared]);

  return (
    <View style={styles.container}>
      <View style={[styles.headerSection, { maxWidth }]}>
        <Text style={styles.title}>{HappyScoreCategoryTitles[screen]}</Text>
      </View>
      {/* <Text style={styles.title}>Overview</Text> */}
      <View style={[styles.contentWrapper, { maxWidth }]}>
        <Animated.View
          style={[
            styles.contentContainer,
            { height: topSectionHeight, marginStart: topSectionMarginStart },
          ]}
        >
          <SectionHeader
            defaultShown={sectionHeaderDefaultShown}
            style={{ paddingStart: sectionHeaderPaddingStart }}
            setDefaultShown={setSectionHeaderDefaultShown}
            title1="Insights"
            title2={HappyScoreCategoryTitles[screen]
              ? `${HappyScoreCategoryTitles[screen]} Graph` : 'Graph'}
            button1="INSIGHTS"
            button2="GRAPH"
            hideGraphButton={graphData[graphIndex].length === 0}
          />
          {sectionHeaderDefaultShown && defaultContentShown && (
            <TopSectionInsights
              {...{
                average, yesterdayScore, screen, todayScore,
              }}
            />
          )}
          <HappyScoreIndicator
            style={[
              styles.happyScoreIndicator,
              {
                transform: [{ scale: happyScoreIndicatorSize }],
                top: happyScoreIndicatorOffset,
                start: happyScoreIndicatorOffset,
              },
            ]}
            happyScoreAnimatedValue={liveScoreAnimatedValue}
            happyScoreValue={liveScore}
            happyScoreComparedAnimatedValue={scoreComparedAnimatedValue}
            happyScoreComparedValue={scoreCompared}
            showComparison={!!compareRange.start && !!compareRange.end}
            noValue={!Number.isFinite(liveScore)}
            noValueCompared={!Number.isFinite(scoreCompared)}
            still={still}
          />
        </Animated.View>
        <Animated.View
          style={[
            styles.graphContainer,
            {
              height: graphContainerHeight,
              marginTop: graphContainerMarginTop,
              opacity: graphContainerOpacity,
            },
          ]}
        >
          {!sectionHeaderDefaultShown && !defaultContentShown && (
            <HappyDetailsGraphWeb
              {...{ graphIndex, graphData, graphCompareData }}
              showComparison={!!compareRange.start && !!compareRange.end} />
          )}
        </Animated.View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    margin: 34,
    zIndex: 2,
  },
  contentWrapper: {
    marginTop: 50,
  },
  contentContainer: {
    height: 327,
    backgroundColor: '#FFF',
    borderRadius: 24,
    paddingTop: 17,
    paddingBottom: 27,
  },
  headerSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 24,
    zIndex: 2,
  },
  title: {
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_400Regular',
    fontSize: 22,
    lineHeight: 33,
    marginBottom: 16,
  },
  graphContainer: {
    borderRadius: 24,
    paddingStart: 24,
    backgroundColor: '#FFF',
    borderColor: Colors.light.hereworksWhite300,
    borderWidth: 1,
  },
  happyScoreIndicator: {
    top: -60,
    start: -60,
  },
  export: {
    height: 38,
    marginStart: 2,
  },
});

export default TopSection;
